import { issuedBatchCode } from "../../commerce/voucher-fragment";

/**
 * A voucher code typed outside the voucher page (the home page's code box), handed to it.
 *
 * A printed code goes in the address fragment, which the voucher page already reads and clears
 * (`consumeVoucherFragment`): a fragment never reaches a server, a query string or a log. Any other
 * code (a practice pass, say) rides in this tab's history state instead, which the voucher page
 * reads once and clears. Nothing here checks or uses the code.
 *
 * Either way the history state also says the customer typed the code and pressed "Use your
 * voucher", so the voucher page checks it straight away instead of asking for a second press
 * (book quality X1, 2026-11-02). Checking never uses a voucher. A code scanned from a voucher card
 * arrives in the fragment without that mark, and still waits for "Check my code".
 */
const MAX_CODE_LENGTH = 64;

/** A printed code as the voucher card shows it, whatever the spacing, dashes or letter case. */
export function formatIssuedVoucherCode(typed: string): string | null {
  const plain = typed.toUpperCase().replace(/[^A-Z0-9]/gu, "");
  if (plain.length < 17) return null;
  const groups = plain.slice(-15);
  const code = [
    plain.slice(0, -15),
    groups.slice(0, 5),
    groups.slice(5, 10),
    groups.slice(10),
  ].join("-");
  return issuedBatchCode.test(code) ? code : null;
}

export type VoucherCodeHandoff = {
  to: { pathname: "/redeem"; hash?: string };
  state: { voucherCode?: string; checkNow: true };
};

/** Where a typed code goes, or null when nothing usable was typed. */
export function voucherCodeHandoff(typed: string): VoucherCodeHandoff | null {
  const trimmed = typed.trim();
  if (!trimmed || trimmed.length > MAX_CODE_LENGTH) return null;
  const issued = formatIssuedVoucherCode(trimmed);
  if (issued)
    return { to: { pathname: "/redeem", hash: "voucher=" + issued }, state: { checkNow: true } };
  return {
    to: { pathname: "/redeem" },
    state: { voucherCode: trimmed.toUpperCase(), checkNow: true },
  };
}

/** A code handed over in history state, or "" when there isn't one. */
export function handedOffVoucherCode(state: unknown): string {
  const code =
    state && typeof state === "object" ? (state as { voucherCode?: unknown }).voucherCode : null;
  if (typeof code !== "string") return "";
  const trimmed = code.trim();
  return trimmed && trimmed.length <= MAX_CODE_LENGTH ? trimmed.toUpperCase() : "";
}

/** Whether a code arriving with this history state was typed and sent to be checked straight away. */
export function handedOffToCheck(state: unknown): boolean {
  return Boolean(
    state && typeof state === "object" && (state as { checkNow?: unknown }).checkNow === true,
  );
}
